import React, { useEffect, useRef, useState } from 'react';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyMedia, MediaResponse } from '@/lib/collections/media';
import { playClick, playHover } from '@/utils/sounds';
import { useAuth } from '@pooflabs/web';
import { Wallet, CheckCircle2, LogOut } from 'lucide-react';

// ─── SVG Icons ────────────────────────────────────────────────────────────────

const PlayIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <polygon points="5,3 19,12 5,21" />
  </svg>
);

const PauseIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);

const SpeakerIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
);

const MutedIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

// ─── Inner controls (accepts a resolved audioUrl) ─────────────────────────────

interface AudioControlsInnerProps {
  audioUrl: string;
}

const AudioControlsInner: React.FC<AudioControlsInnerProps> = ({ audioUrl }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const { user, loading: authLoading, login, logout } = useAuth();
  const [walletHovered, setWalletHovered] = useState(false);

  // Attempt autoplay once the audio element is ready to play
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const tryPlay = () => {
      audio.play()
        .then(() => {
          setPlaying(true);
          setIsMuted(false);
          setShowPulse(false);
        })
        .catch((err: Error) => {
          console.warn('Autoplay blocked:', err.message);
          // Fall back to muted autoplay
          audio.muted = true;
          audio.play()
            .then(() => {
              setPlaying(true);
              setIsMuted(true);
              setShowPulse(true); // hint user to unmute
            })
            .catch((err2: Error) => {
              console.warn('Muted autoplay also blocked:', err2.message);
              setPlaying(false);
            });
        });
    };

    audio.addEventListener('canplay', tryPlay, { once: true });
    // If already loaded enough, fire immediately
    if (audio.readyState >= 3) tryPlay();

    return () => audio.removeEventListener('canplay', tryPlay);
  }, [audioUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    playClick();
    if (audio.paused) {
      audio.muted = false; // unmute on explicit user gesture
      audio.play()
        .then(() => {
          setPlaying(true);
          setIsMuted(false);
          setShowPulse(false);
        })
        .catch((err: Error) => console.error('Play failed:', err.message));
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    playClick();
    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
    if (!audio.muted) setShowPulse(false);
  };

  const btnBase: React.CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: '2.5px solid #38BDF8',
    background: 'rgba(56, 189, 248, 0.15)',
    color: '#38BDF8',
    boxShadow: '0 0 12px rgba(56, 189, 248, 0.4)',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(8px)',
    flexShrink: 0,
    outline: 'none',
    padding: 0,
  };

  return (
    <>
      {/* Hidden audio element — persists in DOM, browser manages its lifecycle */}
      <audio ref={audioRef} src={audioUrl} loop preload="auto" style={{ display: 'none' }} />

      <style>{`
        @keyframes audio-pulse {
          0%, 100% { box-shadow: 0 0 12px rgba(56,189,248,0.4); }
          50% { box-shadow: 0 0 22px rgba(56,189,248,0.75), 0 0 36px rgba(56,189,248,0.3); }
        }
        .audio-btn-muted-hint {
          animation: audio-pulse 1.8s ease-in-out infinite;
        }
        .audio-btn:hover {
          background: rgba(56,189,248,0.28) !important;
          box-shadow: 0 0 20px rgba(56,189,248,0.65) !important;
          transform: scale(1.1);
        }
        .audio-btn:active {
          transform: scale(0.95);
        }
        .wallet-btn:hover {
          transform: scale(1.1) !important;
        }
        .wallet-btn:active {
          transform: scale(0.95) !important;
        }
        @keyframes wallet-warn-pulse {
          0%, 100% { box-shadow: 0 0 12px rgba(230,57,70,0.5), 0 0 0 0 rgba(230,57,70,0.35); }
          50% { box-shadow: 0 0 20px rgba(230,57,70,0.75), 0 0 0 4px rgba(230,57,70,0.12); }
        }
        .wallet-btn-disconnected {
          animation: wallet-warn-pulse 2.2s ease-in-out infinite;
        }
        @media (min-width: 768px) {
          .audio-btn { width: 48px !important; height: 48px !important; }
          .wallet-btn { width: 48px !important; height: 48px !important; }
          .audio-controls-wrap { top: 108px !important; }
        }
      `}</style>
      <div
        className="audio-controls-wrap fixed right-5 z-50 flex flex-col"
        style={{
          top: 68,
          gap: 8,
        }}
      >
        <button
          onClick={togglePlay}
          onMouseEnter={playHover}
          className="audio-btn"
          style={btnBase}
          title={playing ? 'Pause music' : 'Play music'}
          aria-label={playing ? 'Pause background music' : 'Play background music'}
        >
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>

        <button
          onClick={toggleMute}
          onMouseEnter={playHover}
          className={`audio-btn${showPulse && playing ? ' audio-btn-muted-hint' : ''}`}
          style={btnBase}
          title={isMuted ? 'Unmute music' : 'Mute music'}
          aria-label={isMuted ? 'Unmute background music' : 'Mute background music'}
        >
          {isMuted ? <MutedIcon /> : <SpeakerIcon />}
        </button>

        {/* Wallet connect button — separator line */}
        <div
          aria-hidden="true"
          style={{
            width: '60%',
            height: '1px',
            background: 'rgba(255,255,255,0.1)',
            margin: '2px auto 0',
            alignSelf: 'center',
          }}
        />

        {/* Wallet button */}
        {!authLoading && (
          user ? (
            <button
              className="wallet-btn"
              onClick={() => { playClick(); logout(); }}
              onMouseEnter={() => { playHover(); setWalletHovered(true); }}
              onMouseLeave={() => setWalletHovered(false)}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: walletHovered ? '2.5px solid #f87171' : '2.5px solid #7CFC00',
                background: walletHovered ? 'rgba(248,113,113,0.18)' : 'rgba(124, 252, 0, 0.12)',
                color: walletHovered ? '#f87171' : '#7CFC00',
                boxShadow: walletHovered ? '0 0 12px rgba(248,113,113,0.45)' : '0 0 10px rgba(124,252,0,0.35)',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(8px)',
                flexShrink: 0,
                outline: 'none',
                padding: 0,
                position: 'relative',
              }}
              title={walletHovered ? 'Disconnect wallet' : `Connected: ${user.address}`}
              aria-label={walletHovered ? 'Disconnect wallet' : `Wallet connected: ${user.address}`}
            >
              {walletHovered ? <LogOut size={16} /> : <CheckCircle2 size={17} />}
            </button>
          ) : (
            <button
              className="wallet-btn wallet-btn-disconnected"
              onClick={() => { playClick(); login(); }}
              onMouseEnter={playHover}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '2.5px solid #E63946',
                background: 'rgba(230,57,70,0.14)',
                color: '#E63946',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(8px)',
                flexShrink: 0,
                outline: 'none',
                padding: 0,
              }}
              title="Connect wallet"
              aria-label="Connect wallet"
            >
              <Wallet size={16} />
            </button>
          )
        )}
      </div>
    </>
  );
};

// ─── Site-wide FloatingAudioControls (fetches its own audio URL) ───────────────

const FloatingAudioControls: React.FC = () => {
  const { data: mediaEntries } = useRealtimeData<MediaResponse[]>(
    subscribeManyMedia,
    true,
  );
  const audioEntry = mediaEntries?.find(m => m.type === 'audio') ?? null;

  if (!audioEntry?.url) return null;

  return <AudioControlsInner audioUrl={audioEntry.url} />;
};

export default FloatingAudioControls;
